import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Autocomplete, TextField } from "@mui/material";
import { useState } from "react";

import { useGlobalModal } from "../../../components/Contexts/GlobalModalContext";
import { DiggingLocationDTO } from "../../../DTOs/Production/DiggingLocationDTO";

type MoveLoadingRecordModalProps = {
  availableLocations: DiggingLocationDTO[];
  currentLocation: DiggingLocationDTO;
  onMove: (newDiggingPatternId: number) => Promise<boolean>;
}

const MoveLoadingRecordModal: React.FC<MoveLoadingRecordModalProps> = ({
  availableLocations,
  currentLocation,
  onMove
}) => {
  const { hideModal } = useGlobalModal();
  const [isWorking, setIsWorking] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<number>(0);

  return (
    <form>
      <h2 className="border-b border-theme-border font-semibold pb-2 text-[1.6rem] text-theme-text">
        Move Loading Record
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          className={`text-theme-text text-sm transition ease-out mb-2 ml-1 opacity-${isWorking ? 100 : 0}`}
        />
      </h2>
      <div className="pt-2 mb-2">
        <p className="text-sm text-theme-text mb-1"><span className="font-semibold">From: </span>Blastmaster <span className="font-semibold">{currentLocation.blastMasterId ?? currentLocation.patternBlastId}</span> ({currentLocation.pit})</p>
      </div>

      <div className="flex flex-col gap-1 mb-4">
        <p className="text-sm text-theme-text"><span className="font-semibold">To: </span></p>
        <div className="flex flex-col">
          <Autocomplete<DiggingLocationDTO, false, false, false>
            classes={{ root: "w-full" }}
            size="small"
            options={availableLocations}
            value={availableLocations.find(o => o.diggingPatternId === selectedLocationId) ?? null}
            getOptionLabel={(o) => `Blastmaster: ${o.blastMasterId ?? o.patternBlastId} - Pit: ${o.pit}`}
            renderOption={(props, o) => {
              return (
                <li {...props} key={o.diggingPatternId} className="p-2 cursor-pointer hover:bg-theme-surface transition duration-150">
                  Blastmaster: <span className="font-semibold">{o.blastMasterId ?? o.patternBlastId}</span> - Pit: <span className="font-semibold">{o.pit}</span>
                </li>
              )
            }}
            isOptionEqualToValue={(opt, val) => opt.diggingPatternId === val.diggingPatternId}
            onChange={(_, opt) => setSelectedLocationId(opt?.diggingPatternId ?? 0)}
            noOptionsText="No other locations available"
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select blastmaster"
                size="small"
              />
            )}
          />
        </div>
      </div>

      <div className=" flex justify-end gap-2 mt-12">
        <button
          type="button"
          onClick={hideModal}
          className="px-4 py-2 bg-theme-text-muted text-theme-text cursor-pointer hover:bg-theme-text-muted-hover hover:text-theme-text-hover transition rounded-sm border-none"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={async () => {
            if (selectedLocationId === 0) {
              return;
            }
            setIsWorking(true);
            const success = await onMove(selectedLocationId);
            if (success) {
              hideModal();
            }
            setIsWorking(false);
          }}
          disabled={selectedLocationId === 0}
          className="px-4 py-2 bg-theme-info hover:bg-theme-info-hover transition text-white rounded-sm border-none disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
        >
          Move
        </button>
      </div>
    </form>
  );
};

export default MoveLoadingRecordModal;